import React, { useCallback, useState, useMemo, useEffect } from 'react';
import * as PR from "../../../prime-modules/index";
import { useSelector, useDispatch } from 'react-redux';
import { getLanguageTranslates, uploadTranslations } from '../../../services/api';
import ContentBlockItem from './ContentBlockItem';
import "./ContentBlocks.scss";

const ContentBlocks = () => {
    const toast = React.useRef(null);
    const dispatch = useDispatch();
    const adminData = useSelector(state => state.adminAuth.adminSessionData);
    const headers = useMemo(() => {
        return { sessionid: adminData.sessionId };
    }, [adminData.sessionId]);

    const [contentBlocks, setContentBlocks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editKey, setEditKey] = useState("");
    const [addDisable, setAddDisable] = useState(false);

    const fetchContentBlocks = useCallback(() => {
        setLoading(true);
        setEditKey("");
        setAddDisable(false);
        const getResponse = (response) => {
            setLoading(false);
            if (response.result === 'SUCCESS') {
                const assetData = response.data;
                try {
                    const contentBlocksRaw = assetData?.cmsData?.contentBlocks?.selectedLanguage;
                    if (typeof contentBlocksRaw === 'string') {
                        setContentBlocks(contentBlocksRaw ? JSON.parse(contentBlocksRaw) : []);
                    } else if (Array.isArray(contentBlocksRaw)) {
                        setContentBlocks(contentBlocksRaw);
                    } else {
                        setContentBlocks([]);
                    }
                } catch (e) {
                    setContentBlocks([]);
                }
            } else if (response.result === "FAILED" && response.error) {
                const error = response.error;
                toast.current.show({ severity: error.severity, summary: 'Error', detail: error.errorMsg || error.summary });
            }
        };
        getLanguageTranslates('en', headers, dispatch, getResponse);
    }, [dispatch, headers]);

    useEffect(() => {
        fetchContentBlocks();
    }, [fetchContentBlocks]);

    const submitContentBlocksHandler = async (updatedContentBlocks, type = '') => {
        const request = {
            "cmsData": {
                "contentBlocks": JSON.stringify(updatedContentBlocks)
            }
        };
        setLoading(true);
        const getCMSResponse = (response) => {
            setLoading(false);
            setEditKey("");
            setAddDisable(false);
            if (response.result === 'SUCCESS') {
                toast.current.show({ severity: 'success', summary: 'Success', detail: 'Updated successfully' });
                setContentBlocks(updatedContentBlocks);
                fetchContentBlocks();
            } else if (response.result === "FAILED" && response.error) {
                const error = response.error;
                toast.current.show({ severity: error.severity, summary: 'Error', detail: error.errorMsg || error.summary });
            }
        };
        uploadTranslations('en', request, headers, dispatch, getCMSResponse);
    };

    const addNewContentBlock = () => {
        const newContentBlock = {
            title: "",
            image: "",
            link: "",
            id: Date.now().toString()
        };
        setContentBlocks(prev => [...prev, newContentBlock]);
        setAddDisable(true);
        setEditKey(newContentBlock.id);
    };

    const cancelAddNewContentBlock = () => {
        setContentBlocks(prev => prev.filter(s => s.title || s.image || s.link));
        setAddDisable(false);
        setEditKey("");
    };

    const contentBlockSaveHandler = (contentBlock, index, type = '') => {
        let updatedContentBlocks = [...contentBlocks];
        if (type === 'delete') {
            updatedContentBlocks.splice(index, 1);
        } else {
            updatedContentBlocks[index] = { ...contentBlock };
        }
        submitContentBlocksHandler(updatedContentBlocks);
    };

    const swapElements = (arr, i1, i2) => {
        if (i2 < 0 || i2 >= arr.length) return arr;
        const newArr = [...arr];
        [newArr[i1], newArr[i2]] = [newArr[i2], newArr[i1]];
        submitContentBlocksHandler(newArr);
        return newArr;
    };

    const moveUp = (index) => setContentBlocks(prev => swapElements(prev, index, index - 1));
    const moveDown = (index) => setContentBlocks(prev => swapElements(prev, index, index + 1));

    return (
        <div className='general content-blocks-section'>
            <div className="card">
             
                <div className='add-edit-faq'>
                    <div className="paragraph-tag mb-4">
                        <h2>Add/Edit Content Blocks</h2>
                    </div>
                    <div className="mt-3 mb-4">
                        {contentBlocks.map((contentBlock, idx) => (
                            <ContentBlockItem
                                key={contentBlock.id || idx}
                                contentBlock={contentBlock}
                                index={idx}
                                searchValue=""
                                contentBlockSaveHandler={contentBlockSaveHandler}
                                editKey={editKey}
                                setEditKey={setEditKey}
                                moveUp={moveUp}
                                moveDown={moveDown}
                                lastIndex={contentBlocks.length - 1}
                                loading={loading}
                                cancelAddNewContentBlock={cancelAddNewContentBlock}
                            />
                        ))}
                    </div>
                    <PR.Button
                        label='+ Add New Content Block'
                        className="add-faq-button"
                        onClick={addNewContentBlock}
                        disabled={addDisable || editKey || loading}
                    />
                </div>
                {loading && (
                    <div className="progress-spinner">
                        <PR.ProgressSpinner
                            className="progressSpinner"
                            animationDuration=".5s"
                        />
                    </div>
                )}
            </div>
            <PR.Toast ref={toast} position="top-right" />
        </div>
    );
};

export default ContentBlocks;
